const zonedFormatterCache = new Map<string, Intl.DateTimeFormat>();

export type ZonedDateParts = {
  isoDate: string;
  isoTime: string;
  weekday: number;
};

const WEEKDAY_INDEX: Record<string, number> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

export function getZonedFormatter(timezone: string) {
  if (!zonedFormatterCache.has(timezone)) {
    zonedFormatterCache.set(
      timezone,
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        weekday: 'short',
      })
    );
  }

  return zonedFormatterCache.get(timezone)!;
}

export function getZonedDateParts(date: Date, timezone: string): ZonedDateParts {
  const formatter = getZonedFormatter(timezone);
  const parts = formatter.formatToParts(date);
  const lookup: Record<string, string> = {};

  for (const part of parts) {
    if (part.type === 'literal' || lookup[part.type]) {
      continue;
    }

    lookup[part.type] = part.value;
  }

  const weekdayLabel = (lookup.weekday ?? '').slice(0, 3).toLowerCase();
  const weekday = WEEKDAY_INDEX[weekdayLabel];

  if (typeof weekday === 'undefined') {
    throw new Error(`Unable to resolve weekday for timezone ${timezone}`);
  }

  return {
    isoDate: `${lookup.year}-${lookup.month}-${lookup.day}`,
    isoTime: `${lookup.hour}:${lookup.minute}:${lookup.second}`,
    weekday,
  };
}

function getOffsetForDate(date: Date, timezone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const lookup: Record<string, string> = {};

  for (const part of parts) {
    if (part.type !== 'literal') {
      lookup[part.type] = part.value;
    }
  }

  const asUtc = Date.UTC(
    Number.parseInt(lookup.year, 10),
    Number.parseInt(lookup.month, 10) - 1,
    Number.parseInt(lookup.day, 10),
    Number.parseInt(lookup.hour, 10),
    Number.parseInt(lookup.minute, 10),
    Number.parseInt(lookup.second, 10)
  );

  return asUtc - date.getTime();
}

export function zonedDateTimeToUtc(dateString: string, timezone: string) {
  const [datePart, timePart = '00:00:00'] = dateString.split('T');
  const [year, month, day] = datePart.split('-').map((value) => Number.parseInt(value, 10));
  const timeSegments = timePart.split(':').map((value) => Number.parseInt(value, 10));
  const [hour = 0, minute = 0, second = 0] = timeSegments;

  const base = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const offset = getOffsetForDate(base, timezone);
  return new Date(base.getTime() - offset);
}

export function ensureIsoTime(time: string) {
  return time.includes(':') && time.split(':').length === 3 ? time : `${time}:00`;
}

export type DateTimeComponents = {
  dateLabel: string;
  timeLabel: string;
  timeZoneName?: string;
};

export function resolveDateTimeComponents({
  date,
  timezone,
  showTimezone,
}: {
  date: Date | string;
  timezone: string;
  showTimezone: boolean;
}): DateTimeComponents {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: showTimezone ? 'short' : undefined,
  });

  const parts = formatter.formatToParts(new Date(date));
  const partMap = new Map<string, string>();

  for (const part of parts) {
    if (part.type === 'literal') {
      continue;
    }

    if (!partMap.has(part.type)) {
      partMap.set(part.type, part.value);
    }
  }

  const month = partMap.get('month') ?? '';
  const day = partMap.get('day') ?? '';
  const year = partMap.get('year') ?? '';
  const hour = partMap.get('hour') ?? '';
  const minute = partMap.get('minute') ?? '';
  const dayPeriodRaw = partMap.get('dayPeriod');

  const meridiemRaw = dayPeriodRaw ? dayPeriodRaw.toLowerCase().replace(' ', '') : '';
  const meridiem = meridiemRaw.length ? ` ${meridiemRaw}` : '';

  const timeZoneName = showTimezone ? partMap.get('timeZoneName')?.toUpperCase() : undefined;

  return {
    dateLabel: `${month} ${day}, ${year}`,
    timeLabel: `${hour}:${minute}${meridiem}`,
    timeZoneName: timeZoneName && timeZoneName.trim().length ? timeZoneName : undefined,
  };
}

export function assembleDateTimeLabel(components: DateTimeComponents) {
  const timezoneLabel = components.timeZoneName ? ` ${components.timeZoneName}` : '';
  return `${components.dateLabel} at ${components.timeLabel}${timezoneLabel}`;
}

export function formatDateWithTimeZone({
  date,
  timezone,
  showTimezone = true,
}: {
  date: Date | string;
  timezone: string;
  showTimezone?: boolean;
}) {
  const components = resolveDateTimeComponents({
    date,
    timezone,
    showTimezone,
  });

  return assembleDateTimeLabel(components);
}

export function formatDateRangeWithTimeZone({
  start,
  end,
  timezone,
}: {
  start: Date | string;
  end: Date | string;
  timezone: string;
}) {
  const startComponents = resolveDateTimeComponents({
    date: start,
    timezone,
    showTimezone: true,
  });
  const endComponents = resolveDateTimeComponents({
    date: end,
    timezone,
    showTimezone: true,
  });

  const sameDay = startComponents.dateLabel === endComponents.dateLabel;

  if (sameDay) {
    const startLabel = assembleDateTimeLabel(startComponents);
    const endTimezoneLabel = endComponents.timeZoneName ? ` ${endComponents.timeZoneName}` : '';
    return `${startLabel} – ${endComponents.timeLabel}${endTimezoneLabel}`;
  }

  const startLabel = assembleDateTimeLabel(startComponents);
  const endLabel = assembleDateTimeLabel(endComponents);

  return `${startLabel} → ${endLabel}`;
}
